import React, { useState } from 'react'
import { AxiosError } from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { useDropzone } from 'react-dropzone'
import { useSearchParams } from 'react-router-dom'
import StepNavigation from './StepNavigation'
import axiosInstance from '../../../../../utils/axiosInstance'
import { validateFileCount, validateFileSize } from '../../../../../utils/fileValidation'
import {
  ACCEPTED_IMAGE_RATIOS,
  ACCEPTED_VIDEO_RATIOS,
  IMAGE_MIN_WIDTH,
  IMAGE_RATIO_TOLERANCE,
  MediaPlacement,
  PLACEMENT_COLORS,
  PLACEMENT_LABELS,
  SocialMediaAsset,
  VIDEO_MIN_DURATION_SECONDS,
  VIDEO_MIN_WIDTH,
  VIDEO_RATIO_TOLERANCE,
} from '../types'
import { ImageGeneratorDialog } from '../../../../../components/ImageGenerator'
import type { ImageGeneratorOrderData, CardFormat } from '../../../../../components/ImageGenerator'

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

const DropArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragActive',
})<{ isDragActive?: boolean }>(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: '8px',
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isDragActive ? theme.palette.action.hover : theme.palette.background.default,
  transition: 'border-color 0.2s, background-color 0.2s',
  '&:hover': { borderColor: theme.palette.primary.main },
}))

const AssetRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 0),
}))

/** Detect image dimensions from a File using an offscreen <img> */
function detectImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(url)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image dimensions'))
    }
    img.src = url
  })
}

/** Detect video dimensions and duration from a File using an offscreen <video> */
function detectVideoMetadata(
  file: File
): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration })
      URL.revokeObjectURL(url)
    }
    video.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read video metadata'))
    }
    video.src = url
  })
}

/** Returns the placement whose aspect ratio this image matches, or null if none do */
function detectImagePlacement(width: number, height: number): MediaPlacement | null {
  if (width === 0 || height === 0) return null
  const ratio = width / height
  const match = ACCEPTED_IMAGE_RATIOS.find(([r]) => Math.abs(ratio - r) / r < IMAGE_RATIO_TOLERANCE)
  return match ? match[1] : null
}

/** Returns the placement whose aspect ratio this video matches, or null if none do */
function detectVideoPlacement(width: number, height: number): MediaPlacement | null {
  if (width === 0 || height === 0) return null
  const ratio = width / height
  const match = ACCEPTED_VIDEO_RATIOS.find(([r]) => Math.abs(ratio - r) < VIDEO_RATIO_TOLERANCE)
  return match ? match[1] : null
}

function formatAcceptedImageRatios(): string {
  return ACCEPTED_IMAGE_RATIOS.map(([, p, label]) => `${label} (${PLACEMENT_LABELS[p]})`).join(', ')
}

function formatAcceptedVideoRatios(): string {
  return ACCEPTED_VIDEO_RATIOS.map(([, p, label]) => `${label} (${PLACEMENT_LABELS[p]})`).join(', ')
}

interface Props {
  smId: string | null
  mediaAssets: SocialMediaAsset[]
  onAssetsChange: (assets: SocialMediaAsset[]) => void
  onNext: () => void
  onBack: () => void
  orderData?: ImageGeneratorOrderData
  readOnly?: boolean
}

const MediaStep: React.FC<Props> = ({
  smId,
  mediaAssets,
  onAssetsChange,
  onNext,
  onBack,
  orderData,
  readOnly,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [uploading, setUploading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const generatorOpen = searchParams.has('generate-media')

  // An image uploaded next to a video is sent as that video's thumbnail (see
  // resolvedVideoThumbnailUrl in postPublishSocialMarketing), not as its own ad.
  const hasVideo = mediaAssets.some((a) => a.type === 'video')
  const hasImage = mediaAssets.some((a) => a.type === 'image')

  // Which image the server would pick as the thumbnail: it prefers a feed image and
  // otherwise takes the first one. Mirrored here so the list can point at that exact row
  // rather than vaguely saying "an image is used".
  const thumbnailAssetId = (() => {
    if (!hasVideo) return null
    const images = mediaAssets.filter((a) => a.type === 'image')
    const feed = images.find(
      (a) => a.placement === 'feed_square' || a.placement === 'feed_portrait'
    )
    return (feed || images[0])?.id ?? null
  })()

  const openGenerator = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('generate-media', '')
    setSearchParams(newParams, { replace: true })
  }

  const closeGenerator = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('generate-media')
    setSearchParams(newParams, { replace: true })
  }

  const handleAddGeneratedImage = async (file: File, format: CardFormat) => {
    if (!smId) {
      setErrors(['Please fill in campaign details first before adding generated images.'])
      return
    }

    const placementMap: Record<CardFormat, MediaPlacement> = {
      feed: 'feed_square',
      story: 'story',
    }
    const placement = placementMap[format]

    const form = new FormData()
    form.append('file', file)
    form.append('type', 'image')
    form.append('name', file.name)
    form.append('placement', placement)

    try {
      const res = await axiosInstance.post(`/api/social-marketing/${smId}/assets`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onAssetsChange([...mediaAssets, { ...res.data, placement }])
    } catch (error) {
      const serverMessage = (error as AxiosError<{ message?: string }>)?.response?.data?.message
      setErrors([serverMessage || 'Failed to add generated image to campaign media.'])
    }
  }

  const onDrop = async (files: File[]) => {
    if (!smId) {
      setErrors(['Please fill in campaign details first before uploading media.'])
      return
    }

    const newErrors: string[] = []
    const uploaded: SocialMediaAsset[] = []

    setUploading(true)
    setErrors([])

    const countError = validateFileCount(files)
    if (countError) {
      setErrors([countError])
      setUploading(false)
      return
    }

    /* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue */
    for (const file of files) {
      const isVideo = file.type.startsWith('video/')
      let placement: MediaPlacement | null = null

      const sizeError = validateFileSize([file])
      if (sizeError) {
        newErrors.push(sizeError)
        continue
      }

      // Dimension detection & validation
      try {
        if (isVideo) {
          const { width, height, duration } = await detectVideoMetadata(file)
          placement = detectVideoPlacement(width, height)
          if (!placement) {
            newErrors.push(
              `"${
                file.name
              }" was rejected — accepted video ratios: ${formatAcceptedVideoRatios()}. Detected: ${width}×${height}`
            )
            continue
          }
          if (width < VIDEO_MIN_WIDTH) {
            newErrors.push(
              `"${file.name}" was rejected — videos must be at least ${VIDEO_MIN_WIDTH}px wide. Detected: ${width}×${height}`
            )
            continue
          }
          // Meta rejects shorter clips with an opaque server error at publish time.
          if (Number.isFinite(duration) && duration < VIDEO_MIN_DURATION_SECONDS) {
            newErrors.push(
              `"${
                file.name
              }" was rejected — Meta requires ads of at least ${VIDEO_MIN_DURATION_SECONDS} seconds. Detected: ${duration.toFixed(
                1
              )}s`
            )
            continue
          }
        } else {
          const { width, height } = await detectImageDimensions(file)
          placement = detectImagePlacement(width, height)
          if (!placement) {
            newErrors.push(
              `"${
                file.name
              }" was rejected — accepted image ratios: ${formatAcceptedImageRatios()}. Detected: ${width}×${height}`
            )
            continue
          }
          if (width < IMAGE_MIN_WIDTH) {
            newErrors.push(
              `"${file.name}" was rejected — images must be at least ${IMAGE_MIN_WIDTH}px wide. Detected: ${width}×${height}`
            )
            continue
          }
        }
      } catch {
        newErrors.push(`"${file.name}": could not read dimensions. Please try again.`)
        continue
      }

      // Upload
      const type = isVideo ? 'video' : 'image'
      const form = new FormData()
      form.append('file', file)
      form.append('type', type)
      form.append('name', file.name)
      form.append('placement', placement)

      try {
        const res = await axiosInstance.post(`/api/social-marketing/${smId}/assets`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        uploaded.push({ ...res.data, placement })
      } catch (error) {
        const serverMessage = (error as AxiosError<{ message?: string }>)?.response?.data?.message
        newErrors.push(serverMessage || `Failed to upload "${file.name}"`)
      }
    }
    /* eslint-enable no-restricted-syntax, no-await-in-loop, no-continue */

    if (uploaded.length) {
      onAssetsChange([...mediaAssets, ...uploaded])
    }
    setErrors(newErrors)
    setUploading(false)
  }

  const handleRemove = async (assetId: string) => {
    try {
      await axiosInstance.delete(`/api/social-marketing/assets/${assetId}`)
      onAssetsChange(mediaAssets.filter((a) => a.id !== assetId))
    } catch {
      setErrors(['Failed to remove asset'])
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png'],
      'video/*': ['.mp4', '.mov'],
    },
    onDrop,
    multiple: true,
    disabled: readOnly,
  })

  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Upload Media
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AutoFixHighIcon />}
              onClick={openGenerator}
              disabled={readOnly}
            >
              Generate Image
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Supported formats: JPG, PNG, MP4, MOV
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Accepted image ratios: {formatAcceptedImageRatios()}, at least {IMAGE_MIN_WIDTH}px wide
            {' · '}
            Accepted video ratios: {formatAcceptedVideoRatios()}, at least{' '}
            {VIDEO_MIN_DURATION_SECONDS} seconds
          </Typography>

          <DropArea
            {...getRootProps()}
            isDragActive={isDragActive}
            sx={readOnly ? { cursor: 'default', opacity: 0.6, '&:hover': { borderColor: 'divider' } } : undefined}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Uploading…</Typography>
              </Box>
            ) : isDragActive ? (
              <Typography variant="body2">Drop files here…</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {readOnly ? 'Campaign is published — media is read-only' : 'Drag & drop files here, or click to select'}
              </Typography>
            )}
          </DropArea>

          {errors.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {errors.map((e, i) => (
                <Typography key={i} variant="caption" color="error" sx={{ display: 'block' }}>
                  {e}
                </Typography>
              ))}
            </Box>
          )}

          {mediaAssets.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Uploaded Files
              </Typography>
              {mediaAssets.map((asset, idx) => {
                const placement = asset.placement as MediaPlacement | undefined
                const isThumbnail = asset.id === thumbnailAssetId
                // The thumbnail's own placement is irrelevant — it is not shown as an ad
                // in that slot — so labelling it "Feed (Square)" would be misleading.
                const chipColor = isThumbnail
                  ? '#5c6bc0'
                  : (placement && PLACEMENT_COLORS[placement]) || '#888'
                const chipLabel = isThumbnail
                  ? 'Video thumbnail'
                  : (placement && PLACEMENT_LABELS[placement]) || 'Unknown'
                const displayName = asset.file?.filename || asset.name

                return (
                  <React.Fragment key={asset.id}>
                    {idx > 0 && <Divider />}
                    <AssetRow>
                      {asset.type === 'video' ? (
                        <VideocamOutlinedIcon
                          fontSize="small"
                          color="action"
                          sx={{ flexShrink: 0 }}
                        />
                      ) : (
                        <ImageOutlinedIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                      )}

                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={displayName}
                      >
                        {displayName}
                      </Typography>

                      <Chip
                        label={chipLabel}
                        size="small"
                        sx={{
                          backgroundColor: chipColor,
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          flexShrink: 0,
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() => handleRemove(asset.id)}
                        sx={{ flexShrink: 0, color: 'text.secondary' }}
                        disabled={readOnly}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </AssetRow>
                  </React.Fragment>
                )
              })}

              {/* Sits under the file list so it reads as a statement about these files.
                  Without it, an image acting as a thumbnail is indistinguishable from a
                  normal feed image, and there is no hint that uploading one is an option. */}
              {hasVideo && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: '8px',
                    backgroundColor: 'action.hover',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <ImageOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.2 }} />
                  <Typography variant="caption" color="text.secondary">
                    {hasImage ? (
                      <>
                        The image marked <strong>Video thumbnail</strong> is used as the preview
                        image for your video. Remove it to let Meta pick a frame instead.
                      </>
                    ) : (
                      <>
                        <strong>No thumbnail uploaded.</strong> Meta will pick a frame from the
                        video automatically. To choose the preview image yourself, upload an image
                        here as well — it will be used as the thumbnail, not as a separate ad.
                      </>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </SectionCard>

      <StepNavigation onBack={onBack} onNext={onNext} />

      <ImageGeneratorDialog
        open={generatorOpen}
        onClose={closeGenerator}
        orderData={orderData}
        onAddToCampaign={handleAddGeneratedImage}
      />
    </Box>
  )
}

export default MediaStep
